import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface ClarificationCommentFormProps {
  /** Called with the comment text when the form is submitted */
  onSubmit: (content: string) => Promise<void>;
  /** Label shown on the submit button */
  submitLabel: string;
  /** Placeholder text for the textarea */
  placeholder: string;
  /** Called when the cancel button is clicked (only shown when provided) */
  onCancel?: () => void;
}

const ClarificationCommentForm: React.FC<ClarificationCommentFormProps> = ({
  onSubmit,
  submitLabel,
  placeholder,
  onCancel,
}) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(content);
      setContent('');
    } catch (err) {
      console.error('Failed to submit clarification:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Textarea
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="text-sm"
      />
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={!content.trim() || isSubmitting}
          className="bg-gray-600 hover:bg-gray-700 text-white disabled:bg-gray-300 disabled:text-gray-500"
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default ClarificationCommentForm;
